import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { runLoading } from '../../components/loading';

export interface PaymentTerm { id: string; terms: number; }
export interface PaymentTermsSetupRow {
  id: string; client: string | null; clientCode: string | null;
  address: string | null; phone: string | null;
  termsId: string | null; termDays: number | null;
}
export interface Region {
  id: string; regionName: string; description: string | null;
  createdDate: string | null; createdBy: string | null;
  lastUpdatedDate: string | null; lastUpdatedBy: string | null;
}

const PT_KEY = ['acc-payment-terms'];
const PTS_KEY = ['acc-payment-terms-setup'];
const RG_KEY = ['acc-regions'];

// ── Payment Terms ──────────────────────────────────────────────────────────
export function usePaymentTerms() {
  return useQuery({
    queryKey: PT_KEY,
    queryFn: async () =>
      (await api.get<{ data: PaymentTerm[] }>('/api/v1/accounting/payment-terms')).data.data,
  });
}

export function useSavePaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; terms: number }) =>
      v.id
        ? api.put(`/api/v1/accounting/payment-terms/${v.id}`, { terms: v.terms })
        : api.post('/api/v1/accounting/payment-terms', { terms: v.terms }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PT_KEY }),
  });
}

export function useDeletePaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      runLoading(api.delete(`/api/v1/accounting/payment-terms/${id}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PT_KEY }),
  });
}

// ── Payment Terms Setup (per-client assignment) ──────────────────────────────
export function usePaymentTermsSetup() {
  return useQuery({
    queryKey: PTS_KEY,
    queryFn: async () =>
      (await api.get<{ data: PaymentTermsSetupRow[] }>('/api/v1/accounting/payment-terms-setup')).data.data,
  });
}

export function useSetCustomerPaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { customerId: string; termsId: string | null; clientCode?: string | null }) =>
      api.put(`/api/v1/accounting/payment-terms-setup/${v.customerId}`,
        { termsId: v.termsId, clientCode: v.clientCode ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PTS_KEY }),
  });
}

// ── Regions ────────────────────────────────────────────────────────────────
export function useRegions() {
  return useQuery({
    queryKey: RG_KEY,
    queryFn: async () =>
      (await api.get<{ data: Region[] }>('/api/v1/accounting/regions')).data.data,
  });
}

export function useSaveRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; regionName: string; description: string | null }) =>
      v.id
        ? api.put(`/api/v1/accounting/regions/${v.id}`, { regionName: v.regionName, description: v.description })
        : api.post('/api/v1/accounting/regions', { regionName: v.regionName, description: v.description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RG_KEY }),
  });
}

export function useDeleteRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      runLoading(api.delete(`/api/v1/accounting/regions/${id}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RG_KEY }),
  });
}

/** Pull a human-readable message out of an axios error (server validation etc.). */
export function errMessage(e: unknown, fallback = 'Something went wrong.'): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? fallback;
}
